import { Config } from "../config/Config";
import { fetchWithAuth } from "./fetchWithAuth";

const triggerDownload = (href: string, fileName: string) => {
    const link = document.createElement("a");
    link.href = href;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const downloadBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    triggerDownload(url, fileName);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// cProxy's allow-list of markable content types (src/routes/mark.py) is the single source of
// truth - not this file. Unsupported types (413/415) fall back to an unmarked download with a
// warning, mirroring cProxy's own "Sonstige: Warning-Log, kein Standard" behavior.
//
// Exported (not just used internally by exportMedia()) so multi-file flows (e.g. zipping several
// generated/composited images, see utils/fileHandling.tsx downloadFilesAsZip) can mark each file
// individually before bundling them - a zip archive itself has no image content to mark, only the
// files inside it do.
export const markMedia = async (blob: Blob, fileName: string): Promise<Blob> => {
    const baseUrl = Config.json.AiServiceProxyUrl;
    const formData = new FormData();
    formData.append("file", blob, fileName);

    let response: Response;
    try {
        response = await fetchWithAuth(`${baseUrl}/mark`, {
            method: "POST",
            body: formData,
        });
    } catch (err) {
        console.warn(`exportMedia: /mark request failed for "${fileName}", downloading unmarked.`, err);
        return blob;
    }

    if (!response.ok) {
        console.warn(`exportMedia: /mark declined "${fileName}" (HTTP ${response.status}), downloading unmarked.`);
        return blob;
    }

    return response.blob();
};

/**
 * Required by Art. 50 KI-VO for any client-side re-encoded/composited AI media (canvas export,
 * format conversion, upscaling, etc.) before it reaches the user's disk - see CLAUDE.md. Sends
 * the blob through cProxy's /mark endpoint to re-embed the KI-Kennzeichnung lost during
 * re-encoding, then downloads the result.
 */
export const exportMedia = async (blob: Blob, fileName: string): Promise<void> => {
    const markedBlob = await markMedia(blob, fileName);
    downloadBlob(markedBlob, fileName);
};

/**
 * Use when downloading a cProxy result URL unmodified (no re-encoding happens) - the marking
 * cProxy already embedded at generation time is preserved automatically, so this intentionally
 * does NOT call /mark. Re-marking an already-marked, byte-identical file would be redundant.
 */
export const downloadOriginal = async (url: string, fileName: string): Promise<void> => {
    const response = await fetchWithAuth(url);
    const blob = await response.blob();
    downloadBlob(blob, fileName);
};
